import React, {ReactNode, useContext, useState} from "react";
import {
  Box,
  CircularProgress,
  Collapse,
  colors,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { FormattedMessage, useIntl } from "react-intl";
import * as Yup from "yup";
import {FormikHelpers, useFormik} from "formik";
import Grid from "@material-ui/core/Grid";

import { PageTitle } from "../../../../../components/page-title/PageTitle";
import User, { UserScopes } from "../../../../../models/User";
import Document from "../../../../../models/Document";
import ExpandedContent from "../../../components/ExpandedContent";
import ListTile from "../../../components/ListTile";
import { formatPhoneNumber } from "../../../../../common/Hooks";
import { Attachment, AttachmentItem } from "../../../components/Attachment";
import {
  ApproveButton,
  DeclineButton,
  DefaultButton, OutlineButton,
} from "../../../components/Buttons";
import { ErrorWrapper } from "../../../components/ErrorWrapper";
import UserService from "../../../../../services/UserService";
import {NotificationDispatchContext} from "../../../../../context/NotificationContext";

enum LoadingStatus {
  loading,
  failure,
  succeed,
  initial,
  computing,
}

interface PersonalOverviewProps {
  user: User;
  refreshCallback: (user: User) => void
}

const useStyles = makeStyles(
  (theme) => ({
    root: {},
    headerWrapper: {
      display: "flex",
      alignItems: "center",

      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(2),
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(4),
    },
    actionItem: {
      marginLeft: 6,
    },
    errorWrapper: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      paddingBottom: theme.spacing(4),
    },
    extraWrapper: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      backgroundColor: "#fafafa",
      borderTop: "1px solid #e8e8e8"
    },
  }),
  { name: "personal-overview-tab-styles" }
);

export default function PersonalOverview(props: PersonalOverviewProps) {
  const classes = useStyles(props);
  const intl = useIntl();
  // State
  const [user, setUser] = useState(props.user);
  const [canDecline, setCanDecline] = useState(false);
  const [canRequestMore, setCanRequestMore] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(
    (): LoadingStatus => LoadingStatus.initial
  );
  const [openError, setOpenError] = useState(true);
  // Context
  const dispatch = useContext(NotificationDispatchContext);

  const dispatchError = (response: any) => {
    let message = intl.formatMessage({ id: "sharedScreen.unknownError" });
    if(response.data && response.data.data && response.data.data['message']) {
      message = response.data.data['message'];
    }

    dispatch({
      data: {
        type: "error",
        message: message,
      }
    });
  };

  const cancelValidation = () => {
    setCanDecline(false);
    setCanRequestMore(false);
  };

  const updateAfterValidation = () => {
    fetchUser(user, null, () => {
      dispatch({
        data: {
          type: "success",
          message: intl.formatMessage({ id: "sharedScreen.successfulOperation" }),
        }
      });
    });
  };

  const handleAddMessageToDecline = () => {
    setCanDecline(true);
    setCanRequestMore(false);
  };

  const approveUser = async () => {
    setLoadingStatus(LoadingStatus.computing);
    setCanDecline(false);
    setCanRequestMore(false);
    dispatch({
      data: {
        message: null
      }
    });

    let response = await UserService.approveUser(user.id);
    if (response.isSuccess && response.data.code === 100) {
      let user = User.fromJson(JSON.parse(JSON.stringify(response.data.data)));

      fetchUser(user, null, () => {
        dispatch({
          data: {
            type: "success",
            message: intl.formatMessage({ id: "sharedScreen.approveSucceed" }),
          }
        });
      });
    } else {
      setLoadingStatus(LoadingStatus.failure);
      dispatchError(response);
    }
  };

  const handleAddMoreInfo = () => {
    setCanDecline(false);
    setCanRequestMore(true);
  };

  const renderDocAttachments = (docs: Array<Document>, title: ReactNode, subTitle: ReactNode) => {
    if (docs.length === 0) return <></>;

    return (
        <Attachment title={title} subTitle={subTitle}>
          {docs.map((doc) => {
            return doc.paths.map((path) => {
              return (
                  <AttachmentItem src={path}>
                    <Typography gutterBottom variant="h6" component="h6">
                      {intl.formatMessage({ id: doc.document_type.display() })}
                    </Typography>
                  </AttachmentItem>
              );
            });
          })}
        </Attachment>
    );
  };

  const fetchUser = (user: User, errorCallback?: () => void, successCallback?: () => void) => {
    UserService.getById(user.id, `${UserScopes.docs}|${UserScopes.accounts}`).then((response) => {
      if (response.isSuccess && response.data.code === 100) {
        setLoadingStatus(LoadingStatus.succeed);

        let requestUser = User.fromJson(
            JSON.parse(JSON.stringify(response.data.data))
        );

        setUser(requestUser);
        // Call root parent callback
        props.refreshCallback(requestUser);
        if(successCallback !== undefined && successCallback !== null) {
          successCallback();
        }
      } else {
        setLoadingStatus(LoadingStatus.failure);
        dispatchError(response);
        if(errorCallback !== undefined && errorCallback !== null) {
          errorCallback();
        }
      }
    });
  };

  const renderDoc = (docs: Array<Document>) => {
    if (docs.length === 0) return <></>;

    return docs.map((doc) => {
      return (
        <>
          <ListTile
            title={intl.formatMessage({ id: doc.document_type.display() })}
          />
          <ListTile
            title={intl.formatMessage({ id: "sharedScreen.issueDate" })}
          >
            {doc.issueDate()}
          </ListTile>
          <ListTile
            title={intl.formatMessage({ id: "sharedScreen.expirationDate" })}
          >
            {doc.expirationDate()}
          </ListTile>
          <ListTile title={intl.formatMessage({ id: "sharedScreen.idNumber" })}>
            {doc.registration_key}
          </ListTile>
        </>
      );
    });
  };

  const handleErrorWrapper = (state: boolean) => {
    setOpenError(state);
  };

  const renderUser = () => {
    return (
      <Box component="div">
        <Box component="div">
          <ExpandedContent
            title={intl.formatMessage({ id: "sharedScreen.personalInfo" })}
          >
            <ListTile
              title={intl.formatMessage({ id: "sharedScreen.fullName" })}
            >
              {user.fullName()}
            </ListTile>
            <ListTile
              title={intl.formatMessage({ id: "sharedScreen.address" })}
            >
              {user.address()}
            </ListTile>
            <ListTile
              title={intl.formatMessage({ id: "sharedScreen.dateOfBirth" })}
            >
              {user.dateOfBirth()}
            </ListTile>
            <ListTile
              title={intl.formatMessage({ id: "sharedScreen.birthPlace" })}
            >
              {user.birthPlace()}
            </ListTile>
            <ListTile title={intl.formatMessage({ id: "sharedScreen.email" })}>
              {user.email()}
            </ListTile>
            <ListTile
              title={intl.formatMessage({ id: "sharedScreen.phoneNumber" })}
            >
              {formatPhoneNumber(user.telephone())}
            </ListTile>
            <ListTile
              title={intl.formatMessage({
                id: "sharedScreen.otherPhoneNumber",
              })}
            >
              {formatPhoneNumber(user.otherPhoneNumber())}
            </ListTile>
            <ListTile
              title={intl.formatMessage({ id: "sharedScreen.country" })}
            >
              {user.countryStr()}
            </ListTile>
          </ExpandedContent>
        </Box>
        <Box component="div">
          <ExpandedContent
            title={intl.formatMessage({
              id: "sharedScreen.identificationInfo",
            })}
          >
            {renderDoc(user.documents)}
            {renderDocAttachments(
              user.documents,
              intl.formatMessage({ id: "sharedScreen.attachment" }),
              intl.formatMessage({
                id: "sharedScreen.attachmentUserDetails",
              })
            )}
          </ExpandedContent>
        </Box>
      </Box>
    );
  };

  const renderHeaderWrapper = () => {
    const canShowAdditionalButtons = !user.isApproved() && !user.isPending() && !user.isDeclined();

    return (
      <Box component="div">
        <Box component="div" className={classes.headerWrapper}>
          <Box style={{ flexGrow: 1 }}>
            <Typography variant="h4" component="h4">
              <FormattedMessage id="sharedScreen.overview" />
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            <>
              {canShowAdditionalButtons ? (
                <Box component="div" display="flex" alignItems="center">
                  {loadingStatus === LoadingStatus.computing ? (
                      <Box component="div" ml={2} mr={4} mt={1}>
                        <CircularProgress
                            color="secondary"
                            disableShrink={false}
                            size={20}
                            thickness={4}
                        />
                      </Box>
                  ) : (
                      <></>
                  )}

                  <Box
                    component="div"
                    display="inline-block"
                    className={classes.actionItem}
                  >
                    <ApproveButton
                        onClick={(_) => approveUser()}
                        disabled={loadingStatus === LoadingStatus.computing}
                    >
                      <FormattedMessage id="sharedScreen.approve" />
                    </ApproveButton>
                  </Box>
                  {
                    !canDecline ? (
                        <Box
                            component="div"
                            display="inline-block"
                            className={classes.actionItem}
                        >
                          <DeclineButton
                              onClick={(_) => handleAddMessageToDecline()}
                              disabled={loadingStatus === LoadingStatus.computing || user.isDeclined()}
                          >
                            <FormattedMessage id="sharedScreen.decline" />
                          </DeclineButton>
                        </Box>
                    ) : (<></>)
                  }
                  {
                    !canRequestMore ? (
                        <>
                          <Box component="div" ml={2} mr={1}>
                            <Typography
                                component="span"
                                style={{ color: colors.grey[600], fontSize: "0.8rem" }}
                            >
                              <FormattedMessage id="sharedScreen.or" />
                            </Typography>
                          </Box>
                          <Box
                              mr={2}
                              component="div"
                              display="inline-block"
                              className={classes.actionItem}
                          >
                            <DefaultButton
                                onClick={(_) => handleAddMoreInfo()}
                                disabled={loadingStatus === LoadingStatus.computing}
                            >
                              <FormattedMessage id="sharedScreen.requestMoreInfo" />
                            </DefaultButton>
                          </Box>
                        </>
                    ) : (<></>)
                  }
                </Box>
              ) : (
                <></>
              )}
            </>
          </Box>
        </Box>
        <Collapse in={canDecline} timeout="auto" unmountOnExit>
          <Box className={classes.extraWrapper}>
            <AccountValidation
                user={user}
                type="decline"
                onCancel={cancelValidation}
                onUpdateCallback={updateAfterValidation}
            />
          </Box>
        </Collapse>
        <Collapse in={canRequestMore} timeout="auto" unmountOnExit>
          <Box className={classes.extraWrapper}>
            <AccountValidation
                user={user}
                type="request_more_info"
                onCancel={cancelValidation}
                onUpdateCallback={updateAfterValidation}
            />
          </Box>
        </Collapse>
        {renderErrorWrapper()}
      </Box>
    );
  };

  const renderErrorWrapper = () => {
    if (user.isPending()) {
      return (
        <Collapse in={openError} timeout="auto" unmountOnExit>
          <Box component="div" className={classes.errorWrapper}>
            <ErrorWrapper
              onClose={() => handleErrorWrapper(false)}
              title={intl.formatMessage({
                id: "sharedScreen.waitingMoreInfos",
              })}
              text={user.getMessage()}
            />
          </Box>
        </Collapse>
      );
    }

    if (user.isDeclined()) {
      return (
        <Collapse in={openError} timeout="auto" unmountOnExit>
          <Box component="div" className={classes.errorWrapper}>
            <ErrorWrapper
              onClose={() => handleErrorWrapper(false)}
              title={intl.formatMessage({ id: "sharedScreen.rejectedAccount" })}
              text={user.getMessage()}
            />
          </Box>
        </Collapse>
      );
    }

    return <></>;
  };

  return (
    <>
      <PageTitle
        withAppTitle={false}
        title={`${intl.formatMessage({
          id: "sharedScreen.admin",
        })} - ${intl.formatMessage({
          id: "sharedScreen.personalAccountOverview",
        })}`}
      />
      <Box component="div" className={classes.root}>
        {renderHeaderWrapper()}
        {renderUser()}
      </Box>
    </>
  );
}

const useDeclineStyles = makeStyles(
    (theme: any) => ({
      root: {},
      title: {
        fontSize: "1rem",
        fontWeight: theme.typography.fontWeightBold,
        marginTop: "8px",
        marginBottom: "20px",
      },
      textarea: {
        width: "100%",
        height: "200px",
        border: `1px solid ${theme.palette.border.input}`,
        outline: 0,
        padding: "14px",

        "&:hover": {
          border: `1px solid ${theme.palette.border.input}`,
          outline: 0,
        },
      },
    }),
    { name: "account-validation-style" }
);

type ActionType = "decline" | "request_more_info"

interface AccountValidationProps {
  type: ActionType;
  user: User;
  onCancel: () => void;
  onUpdateCallback: () => void
}

function AccountValidation(props: AccountValidationProps) {
  const classes = useDeclineStyles();
  const intl = useIntl();
  const {type, onCancel, user, onUpdateCallback} = props;

  const formik = useFormik({
    initialValues: {
      comment: "",
    },
    validationSchema: Yup.object().shape({}),
    onSubmit: async (
        values: any,
        { setErrors, setSubmitting }: FormikHelpers<any>) => {
      try {
        let response = null;

        if(type === "decline") {
          response = await UserService.declineUser(
              user.id,
              formik.values.comment
          );
        } else {
          response = await UserService.moreInfo(
              user.id,
              formik.values.comment
          );
        }

        if (response.isSuccess) {
          if (response.data.code === 150) {
            let errors = JSON.parse(JSON.stringify(response.data.data));
            setErrors({ ...errors });
            setSubmitting(false);
          }

          if (response.data.code === 100) {
            onUpdateCallback();
            onCancel();
          }

          if (response.data.code === 101) {
            setErrors({ unknown: response.data.message });
            setSubmitting(false);
          }
          return;
        }
      } catch (error) {
        console.dir(error);
      }

      setSubmitting(false);
      setErrors({
        unknown: intl.formatMessage({ id: "error.error" }),
      });
    },
  });

  const renderTitle = () => {
    return (
        <Typography component="p" className={classes.title}>
          {
            type === "decline" ? (
                <FormattedMessage id="sharedScreen.declineAccount" />
            ) : (
                <FormattedMessage id="sharedScreen.requestMoreInfoForValidation" />
            )
          }
        </Typography>
    )
  };

  const genericError = () => {
    if (formik.errors.unknown) {
      return formik.errors.unknown as string;
    }

    if (formik.errors.comment) {
      return formik.errors.comment as string;
    }

    return "";
  };

  return (
      <Box className={classes.root}>
        {renderTitle()}
        <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
          <Grid container component="div" spacing={4}>
            <Grid component="div" item sm={12}>
              <Box component="div">
               <textarea
                   className={classes.textarea}
                   id="comment"
                   name="comment"
                   placeholder={intl.formatMessage({
                     id: "sharedScreen.tellDeclineOrRequestMoreReason",
                   })}
                   value={formik.values.comment}
                   onChange={formik.handleChange}
                   disabled={formik.isSubmitting}
               />
              </Box>
              <Box mt={1}>
                {formik.errors ? (
                    <Typography
                        variant="h6"
                        component="h6"
                        color="error"
                        style={{ paddingRight: "14px" }}
                    >
                      {genericError()}
                    </Typography>
                ) : (
                    <></>
                )}
              </Box>
              <Box mt={2} mb={2} display="flex" alignItems="center">
                <Box>
                  <DefaultButton type="submit" disabled={formik.isSubmitting}>
                    {
                      type === "decline" ? (
                          <FormattedMessage id="sharedScreen.decline" />
                      ) : (
                          <FormattedMessage id="sharedScreen.requestMoreInfo" />
                      )
                    }
                  </DefaultButton>
                </Box>
                <Box ml={1}>
                  <OutlineButton type="button" disabled={formik.isSubmitting} onClick={(_) => onCancel()}>
                    <FormattedMessage id="sharedScreen.cancel" />
                  </OutlineButton>
                </Box>
                {formik.isSubmitting ? (
                    <Box
                        component="span"
                        ml={2}
                        mt={1}
                        display="inline-block"
                    >
                      <CircularProgress
                          color="secondary"
                          disableShrink={false}
                          size={20}
                          thickness={4}
                      />
                    </Box>
                ) : (
                    <></>
                )}
              </Box>
            </Grid>
          </Grid>
        </form>
      </Box>
  );
}